<div class="card">
  <div class="card-body">
    @if (canNavigateToJobs) {
      <a [routerLink]="'/jobs'" class="card-title h5">
        <span class="material-symbols-outlined">analytics</span>
        <span style="vertical-align: middle" class="ms-2">{{ configuration.title || ('dashboard.jobResultAnalytics.title' | translate) }}</span>
        <span class="badge rounded-pill text-bg-warning ms-2 beta-badge">
          {{ 'dashboard.badges.beta' | translate }}
        </span>
      </a>
    } @else {
      <div class="card-title h5">
        <span class="material-symbols-outlined">analytics</span>
        <span style="vertical-align: middle" class="ms-2">{{ configuration.title || ('dashboard.jobResultAnalytics.title' | translate) }}</span>
        <span class="badge rounded-pill text-bg-warning ms-2 beta-badge">
          {{ 'dashboard.badges.beta' | translate }}
        </span>
      </div>
    }
    @if (configuration.description) {
      <h6 class="card-subtitle mb-2 mt-2 text-muted">{{ configuration.description }}</h6>
    }

    <app-loading-indicator [loading]="isLoading"></app-loading-indicator>

    @if (!isLoading) {
      <div class="job-result-analytics-widget">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="total-jobs">
            @if (isBucketedAverageConfiguration()) {
              <span class="label">{{ 'dashboard.jobResultAnalytics.totalVolume' | translate }}:</span>
              <span class="value ms-2">{{ formatValue(bucketedAverage?.total || 0) }}</span>
            } @else {
              <span class="label">{{ 'dashboard.jobResultAnalytics.totalJobs' | translate }}:</span>
              <span class="value ms-2">{{ totalJobCount }}</span>
            }
          </div>
          <div class="date-range text-muted small">
            <span class="material-symbols-outlined" style="font-size: 16px; vertical-align: middle;">calendar_today</span>
            @if (isCustomRange()) {
              <span class="ms-1">{{ getDateRangeDisplay() }}</span>
            } @else {
              <span class="ms-1">{{ 'dashboard.edit.JobDashboardComponent.rangeOptions.' + getRangeKey() | translate }}</span>
            }
          </div>
        </div>

        @if (isDateGrouping()) {
          <div class="text-muted small mb-2">
            {{ 'dashboard.jobResultAnalytics.groupedByDate' | translate: { granularity: getDateGranularityLabel() } }}
          </div>
        }

        @if (error) {
          <div class="alert alert-danger py-2 mb-3">
            <span class="material-symbols-outlined me-2" style="font-size: 18px; vertical-align: middle;">error</span>
            {{ error }}
          </div>
        }

        @if (warnings.length > 0) {
          <div class="warnings mb-3">
            @for (warning of warnings; track warning) {
              <div class="alert alert-warning py-1 mb-1">{{ warning }}</div>
            }
          </div>
        }

        @if (isBucketedAverageConfiguration()) {
          @if (!hasBucketedAverageMetricConfiguration()) {
            <div class="no-data">
              {{ 'dashboard.jobResultAnalytics.noBucketedAverageConfiguration' | translate }}
            </div>
          } @else if (!bucketedAverage) {
            <div class="no-data">
              {{ 'dashboard.jobResultAnalytics.noData' | translate }}
            </div>
          } @else {
            <div class="bucketed-average-kpi">
              <div class="primary-kpi">
                <div class="primary-kpi-value">{{ formatValue(bucketedAverage.averagePerBucket) }}</div>
                <div class="primary-kpi-label">
                  {{ 'dashboard.jobResultAnalytics.averagePerBucket' | translate: { granularity: getBucketGranularityLabel() } }}
                </div>
              </div>

              <div class="kpi-details">
                <div>
                  <span class="detail-label">{{ 'dashboard.jobResultAnalytics.totalVolume' | translate }}</span>
                  <span class="detail-value">{{ formatValue(bucketedAverage.total) }}</span>
                </div>
                <div>
                  <span class="detail-label">{{ 'dashboard.jobResultAnalytics.bucketCount' | translate }}</span>
                  <span class="detail-value">{{ bucketedAverage.bucketCount }}</span>
                </div>
                <div>
                  <span class="detail-label">{{ 'dashboard.jobResultAnalytics.nonEmptyBucketCount' | translate }}</span>
                  <span class="detail-value">{{ bucketedAverage.nonEmptyBucketCount }}</span>
                </div>
                <div>
                  <span class="detail-label">{{ 'dashboard.jobResultAnalytics.emptyBucketMode' | translate }}</span>
                  <span class="detail-value">{{ getEmptyBucketModeLabel() }}</span>
                </div>
              </div>

              @if (bucketedAverage.buckets.length > 0) {
                <div class="bucket-trend">
                  @for (bucket of bucketedAverage.buckets; track bucket.key) {
                    <div class="bucket-row">
                      <div class="bucket-label">{{ bucket.key }}</div>
                      <div class="bucket-bar-container">
                        <div
                          class="bucket-bar"
                          [style.width.%]="getBucketBarWidth(bucket)"
                          [title]="bucket.key + ': ' + formatValue(bucket.value)">
                        </div>
                      </div>
                      <div class="bucket-value">{{ formatValue(bucket.value) }}</div>
                    </div>
                  }
                </div>
              } @else {
                <div class="no-data">
                  {{ 'dashboard.jobResultAnalytics.noData' | translate }}
                </div>
              }
            </div>
          }
        } @else {
          @if (configuration.aggregations && configuration.aggregations.length === 0) {
            <div class="no-data">
              {{ 'dashboard.jobResultAnalytics.noAggregations' | translate }}
            </div>
          } @else if (data.length === 0) {
            <div class="no-data">
              {{ 'dashboard.jobResultAnalytics.noData' | translate }}
            </div>
          } @else {
            <!-- Table View -->
            @if (configuration.chartType === ChartType.TABLE) {
              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>{{ 'dashboard.jobResultAnalytics.group' | translate }}</th>
                      @for (key of getAggregationKeys(); track key) {
                        <th class="text-end">{{ getAggregationLabel(key) }}</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of data; track row.groupKey) {
                      <tr>
                        <td>{{ row.groupKey }}</td>
                        @for (key of getAggregationKeys(); track key) {
                          <td class="text-end">{{ formatValue(row.aggregations[key]) }}</td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }

            <!-- Bar Chart View -->
            @if (configuration.chartType === ChartType.BAR_CHART) {
              <div class="bar-chart">
                @for (row of data; track row.groupKey; let i = $index) {
                  <div class="bar-row">
                    <div class="bar-label">{{ row.groupKey }}</div>
                    <div class="bar-container">
                      @for (key of getAggregationKeys(); track key; let j = $index) {
                        <div
                          class="bar"
                          [style.width.%]="getBarWidthForRow(row, key)"
                          [style.background-color]="getPieChartColor(j)"
                          [title]="getAggregationLabel(key) + ': ' + formatValue(row.aggregations[key])">
                        </div>
                      }
                    </div>
                    <div class="bar-value">
                      @for (key of getAggregationKeys(); track key) {
                        <span>{{ formatValue(row.aggregations[key]) }}</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Pie Chart View -->
            @if (configuration.chartType === ChartType.PIE_CHART) {
              @if (pieSegments.length === 0) {
                <div class="no-data">
                  {{ 'dashboard.jobResultAnalytics.noData' | translate }}
                </div>
              } @else {
                <div class="pie-chart-container">
                  <div class="pie-chart-wrapper">
                    <div class="pie-chart" [style.background]="pieChartBackground">
                      <div class="pie-chart-total">
                        <div class="pie-chart-total-value">{{ formatValue(pieTotal) }}</div>
                        <div class="pie-chart-total-label">{{ pieAggregationLabel }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="pie-legend">
                    @for (segment of pieSegments; track segment.groupKey) {
                      <div class="legend-item">
                        <span class="legend-color" [style.background-color]="segment.color"></span>
                        <span class="legend-label">{{ segment.groupKey }}</span>
                        <span class="legend-value">{{ formatValue(segment.value) }}</span>
                        <span class="legend-percentage">{{ segment.percentage.toFixed(1) }}%</span>
                      </div>
                    }
                  </div>
                </div>
              }
            }
          }
        }
      </div>
    }
  </div>
</div>
